import { useState, useRef, useEffect, useCallback, type KeyboardEvent } from 'react';
import {
  searchAirports,
  searchAirportsByRegion,
  getAirportRegions,
  type Airport,
} from '@flymind/shared';
import { useTranslation } from '../hooks/useTranslation';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function countryToFlag(countryCode: string): string {
  return countryCode
    .toUpperCase()
    .split('')
    .map((char) => String.fromCodePoint(0x1f1e6 + char.charCodeAt(0) - 65))
    .join('');
}

const REGION_LABEL_KEYS: Record<string, string> = {
  all: 'airportSearch.regionAll',
  europe: 'airportSearch.regionEurope',
  north_america: 'airportSearch.regionNorthAmerica',
  south_america: 'airportSearch.regionSouthAmerica',
  asia: 'airportSearch.regionAsia',
  africa: 'airportSearch.regionAfrica',
  oceania: 'airportSearch.regionOceania',
  middle_east: 'airportSearch.regionMiddleEast',
};

const REGION_COLORS: Record<string, string> = {
  europe: 'bg-blue-100 text-blue-700',
  north_america: 'bg-red-100 text-red-700',
  south_america: 'bg-amber-100 text-amber-700',
  asia: 'bg-emerald-100 text-emerald-700',
  africa: 'bg-orange-100 text-orange-700',
  oceania: 'bg-cyan-100 text-cyan-700',
  middle_east: 'bg-purple-100 text-purple-700',
};

const MAX_VISIBLE = 15;
const DEBOUNCE_MS = 200;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface AirportSearchProps {
  value: Airport | null;
  onChange: (airport: Airport | null) => void;
  placeholder?: string;
  label?: string;
  error?: string;
}

export function AirportSearch({
  value,
  onChange,
  placeholder,
  label,
  error,
}: AirportSearchProps) {
  const { t } = useTranslation();
  const resolvedPlaceholder = placeholder ?? t('airportSearch.placeholder');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Airport[]>([]);
  const [totalMatches, setTotalMatches] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [selectedRegion, setSelectedRegion] = useState('all');

  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const regions = ['all', ...getAirportRegions()];

  // ---- Debounced search ----
  const performSearch = useCallback(
    (q: string, region: string) => {
      if (q.trim().length === 0) {
        setResults([]);
        setTotalMatches(0);
        setIsOpen(false);
        setIsSearching(false);
        return;
      }

      // Fetch more than MAX_VISIBLE so we know if there are extra results
      const fetchLimit = MAX_VISIBLE + 1;

      let found: Airport[];
      if (region === 'all') {
        found = searchAirports(q, fetchLimit);
      } else {
        // Filter region airports by search query client-side
        const regionAirports = searchAirportsByRegion(region);
        const allMatches = searchAirports(q, 3000);
        const regionCodes = new Set(regionAirports.map((a) => a.code));
        found = allMatches.filter((a) => regionCodes.has(a.code)).slice(0, fetchLimit);
      }

      const hasMore = found.length > MAX_VISIBLE;
      const visible = found.slice(0, MAX_VISIBLE);

      setResults(visible);
      setTotalMatches(hasMore ? MAX_VISIBLE + 1 : visible.length);
      setIsOpen(visible.length > 0);
      setHighlightedIndex(-1);
      setIsSearching(false);
    },
    [],
  );

  const handleSearch = useCallback(
    (q: string) => {
      setQuery(q);

      if (q.trim().length === 0) {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        setResults([]);
        setTotalMatches(0);
        setIsOpen(false);
        setIsSearching(false);
        return;
      }

      setIsSearching(true);

      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        performSearch(q, selectedRegion);
      }, DEBOUNCE_MS);
    },
    [performSearch, selectedRegion],
  );

  // Re-run search when region changes (if there's an active query)
  useEffect(() => {
    if (query.trim().length > 0) {
      performSearch(query, selectedRegion);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRegion]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const selectAirport = useCallback(
    (airport: Airport) => {
      onChange(airport);
      setQuery('');
      setResults([]);
      setTotalMatches(0);
      setIsOpen(false);
      setHighlightedIndex(-1);
    },
    [onChange],
  );

  const clearSelection = useCallback(() => {
    onChange(null);
    setQuery('');
    inputRef.current?.focus();
  }, [onChange]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (!isOpen || results.length === 0) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightedIndex((prev) =>
            prev < results.length - 1 ? prev + 1 : 0,
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightedIndex((prev) =>
            prev > 0 ? prev - 1 : results.length - 1,
          );
          break;
        case 'Enter':
          e.preventDefault();
          if (highlightedIndex >= 0 && highlightedIndex < results.length) {
            selectAirport(results[highlightedIndex]);
          }
          break;
        case 'Escape':
          setIsOpen(false);
          setHighlightedIndex(-1);
          break;
      }
    },
    [isOpen, results, highlightedIndex, selectAirport],
  );

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const item = listRef.current.children[highlightedIndex] as HTMLElement;
      item?.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex]);

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const inputId = label?.toLowerCase().replace(/\s+/g, '-');

  return (
    <div className="flex flex-col gap-1.5" ref={containerRef}>
      {label && (
        <label htmlFor={inputId} className="text-sm font-medium text-text-primary">
          {label}
        </label>
      )}

      {value ? (
        <div className="flex items-center gap-2 rounded-lg border border-primary-300 bg-primary-50 px-3 py-2">
          <span className="text-lg">{countryToFlag(value.country)}</span>
          <span className="flex-1 text-sm font-medium text-text-primary">
            {value.city} - {value.name} ({value.code})
          </span>
          <button
            type="button"
            onClick={clearSelection}
            className="shrink-0 rounded-full p-0.5 text-text-secondary hover:bg-primary-100 hover:text-text-primary"
            aria-label={t('airportSearch.removeSelection')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
              <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
            </svg>
          </button>
        </div>
      ) : (
        <div className="relative">
          <div className="relative">
            <input
              ref={inputRef}
              id={inputId}
              type="text"
              value={query}
              onChange={(e) => handleSearch(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={() => {
                if (query.trim().length > 0 && results.length > 0) {
                  setIsOpen(true);
                }
              }}
              placeholder={resolvedPlaceholder}
              aria-invalid={error ? 'true' : undefined}
              aria-describedby={error ? `${inputId}-error` : undefined}
              autoComplete="off"
              className={`w-full rounded-lg border bg-surface px-3 py-2 pr-8 text-sm text-text-primary placeholder:text-text-secondary transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                error ? 'border-alert-exceptional' : 'border-border'
              }`}
            />
            {isSearching && (
              <div className="absolute right-2.5 top-1/2 -translate-y-1/2">
                <svg
                  className="h-4 w-4 animate-spin text-text-secondary"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
              </div>
            )}
          </div>

          {isOpen && (
            <div className="absolute z-50 mt-1 w-full rounded-lg border border-border bg-surface shadow-lg">
              {/* Region filter pills */}
              <div className="flex flex-wrap gap-1 border-b border-border px-2 py-2">
                {regions.map((region) => (
                  <button
                    key={region}
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      setSelectedRegion(region);
                    }}
                    className={`rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors ${
                      selectedRegion === region
                        ? 'bg-primary-500 text-white'
                        : 'bg-surface-secondary text-text-secondary hover:bg-surface-tertiary hover:text-text-primary'
                    }`}
                  >
                    {REGION_LABEL_KEYS[region] ? (t as (k: string) => string)(REGION_LABEL_KEYS[region]) : region}
                  </button>
                ))}
              </div>

              {/* Results list */}
              {results.length > 0 ? (
                <ul
                  ref={listRef}
                  className="max-h-80 overflow-auto"
                  role="listbox"
                >
                  {results.map((airport, index) => (
                    <li
                      key={airport.code}
                      role="option"
                      aria-selected={index === highlightedIndex}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        selectAirport(airport);
                      }}
                      onMouseEnter={() => setHighlightedIndex(index)}
                      className={`flex cursor-pointer items-center gap-3 px-3 py-2.5 text-sm transition-colors ${
                        index === highlightedIndex
                          ? 'bg-primary-50 text-text-primary'
                          : 'text-text-primary hover:bg-surface-secondary'
                      }`}
                    >
                      <span className="text-lg shrink-0">{countryToFlag(airport.country)}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold truncate">{airport.city}</span>
                          <span className="shrink-0 text-xs text-text-secondary font-mono">
                            {airport.code}
                          </span>
                        </div>
                        <div className="text-xs text-text-secondary truncate">
                          {airport.name}
                        </div>
                      </div>
                      {airport.region && (
                        <span
                          className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium ${
                            REGION_COLORS[airport.region] || 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {REGION_LABEL_KEYS[airport.region] ? (t as (k: string) => string)(REGION_LABEL_KEYS[airport.region]) : airport.region}
                        </span>
                      )}
                    </li>
                  ))}
                  {totalMatches > MAX_VISIBLE && (
                    <li className="px-3 py-2 text-center text-xs text-text-secondary italic border-t border-border">
                      {t('airportSearch.keepTyping')}
                    </li>
                  )}
                </ul>
              ) : (
                <div className="px-3 py-4 text-center text-sm text-text-secondary">
                  {t('airportSearch.noResults')}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {error && (
        <p id={`${inputId}-error`} className="text-xs text-alert-exceptional">
          {error}
        </p>
      )}
    </div>
  );
}
